import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar, Filter, Download, TrendingUp, FileText, Award, Users, BarChart3,
  Clock, Zap, AlertTriangle, GraduationCap, Activity, Search, RefreshCw,
} from 'lucide-react';
import { toast } from 'react-toastify';
import useSuperAdminReportStore from '@/stores/SuperAdminStore/SuperAdminReportStore';
import { GenerateReportParams } from '@/services/PagesServices/SuperAdminReportService';
import { createSchedule } from '@/services/PagesServices/ReportSchedulerService';
import { ScheduleFrequency } from '@/types/reportScheduler';
import {
  ChartCard, StatCard, InsightStrip, Insight,
} from './components/reportShared';
import {
  DepartmentBar, SkillsRadar, CompletionArea, CertPie, FunnelViz, TimeHistogram,
  LevelDonut, AssessmentDist, ActivityHeatmap, CohortHeatmap, CertExpiryPanel,
  StreakList, TeamTable,
} from './components/ReportCharts';
import './ReportAnalytics.css';

// All report sections offered in the generate modal.
const ALL_SECTIONS = [
  'User Activity Overview',
  'Engagement Metrics',
  'Login Statistics',
  'Time Spent Analysis',
  'Most Active Users',
];

// Map a report card's display name to the backend reportType.
const REPORT_TYPES: Record<string, GenerateReportParams['reportType']> = {
  'Learning Engagement Report': 'learning-engagement',
  'Course Completion Report': 'course-completion',
  'Skills Assessment Report': 'skills-assessment',
  'User Report': 'user',
};

const REPORT_CARDS: Array<{ name: string; description: string; icon: React.ReactNode; tone: string }> = [
  { name: 'Learning Engagement Report', description: 'Detailed analysis of user engagement and activity', icon: <TrendingUp size={24} />, tone: 'blue' },
  { name: 'Course Completion Report', description: 'Track completion rates across all courses', icon: <Award size={24} />, tone: 'green' },
  { name: 'Skills Assessment Report', description: 'Analyze skill development and competency levels', icon: <BarChart3 size={24} />, tone: 'purple' },
  { name: 'User Report', description: 'Overview of users with certifications and achievements', icon: <Users size={24} />, tone: 'orange' },
];

const FALLBACK_DEPARTMENTS = ['Engineering', 'Marketing', 'Sales', 'HR', 'Finance', 'Operations'];
const LEADERBOARD_PAGE_SIZE = 10;

// percent change vs previous period (null when no baseline)
const pctChange = (cur: number, prev: number): number | null =>
  prev > 0 ? Math.round(((cur - prev) / prev) * 100) : null;

const ReportsAnalytics: React.FC = () => {
  const {
    leaderboardData,
    departmentPerformance,
    skillsAssessment,
    completionTrends,
    certificationDistribution,
    generating,
    fetchLeaderboardData,
    fetchDepartmentPerformance,
    fetchSkillsAssessment,
    fetchCompletionTrends,
    fetchCertificationDistribution,
    generateAndDownloadReport,
    // extended analytics
    kpiSummary,
    departments,
    enrollmentFunnel,
    timeDistribution,
    activityHeatmap,
    levelDistribution,
    certificationExpiry,
    assessmentScores,
    cohortRetention,
    teamPerformance,
    analyticsLoading,
    fetchExtendedAnalytics,
  } = useSuperAdminReportStore();

  const [showModal, setShowModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState('');
  const [selectedFormats, setSelectedFormats] = useState<string[]>(['PDF']);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [showBasicConfig, setShowBasicConfig] = useState(true);
  const [showFiltersSegments, setShowFiltersSegments] = useState(false);
  const [selectedSections, setSelectedSections] = useState<string[]>([...ALL_SECTIONS]);

  // Date range for the report
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Right-panel collapsible sections
  const [showReportSectionsPanel, setShowReportSectionsPanel] = useState(true);
  const [showSchedulePanel, setShowSchedulePanel] = useState(true);

  // Automatic scheduling
  const [schedulingEnabled, setSchedulingEnabled] = useState(false);
  const [scheduleFrequency, setScheduleFrequency] = useState<ScheduleFrequency>('weekly');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [scheduleRecipients, setScheduleRecipients] = useState('');

  // Leaderboard: real pagination + search
  const [lbPage, setLbPage] = useState(1);
  const [lbSearch, setLbSearch] = useState('');

  // Fetch data on component mount
  useEffect(() => {
    if (!leaderboardData) fetchLeaderboardData();
    if (!departmentPerformance) fetchDepartmentPerformance();
    if (!skillsAssessment) fetchSkillsAssessment();
    if (!completionTrends) fetchCompletionTrends();
    if (!certificationDistribution) fetchCertificationDistribution();
    if (!kpiSummary) fetchExtendedAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deptOptions = departments && departments.length ? departments : FALLBACK_DEPARTMENTS;

  // --- Derived insights -----------------------------------------------------
  const insights = useMemo<Insight[]>(() => {
    const out: Insight[] = [];
    if (kpiSummary) {
      const wow = pctChange(kpiSummary.activeThisWeek, kpiSummary.activePrevWeek);
      if (wow !== null && wow >= 5) {
        out.push({ tone: 'positive', icon: <TrendingUp size={15} />, text: `Weekly active learners up ${wow}% vs last week.` });
      } else if (wow !== null && wow <= -5) {
        out.push({ tone: 'warning', icon: <TrendingUp size={15} />, text: `Weekly active learners down ${Math.abs(wow)}% vs last week.` });
      }
      if (kpiSummary.completionRate < 40 && kpiSummary.totalEnrollments > 0) {
        out.push({ tone: 'warning', icon: <AlertTriangle size={15} />, text: `Only ${kpiSummary.completionRate}% of enrolments are completed — consider reminders or shorter courses.` });
      } else if (kpiSummary.completionRate >= 70) {
        out.push({ tone: 'positive', icon: <Award size={15} />, text: `Strong ${kpiSummary.completionRate}% overall completion rate.` });
      }
      if (kpiSummary.certsExpiringSoon > 0) {
        out.push({ tone: 'warning', icon: <AlertTriangle size={15} />, text: `${kpiSummary.certsExpiringSoon} certificate${kpiSummary.certsExpiringSoon > 1 ? 's' : ''} expiring within 30 days.` });
      }
    }
    if (enrollmentFunnel && enrollmentFunnel.enrolled > 0) {
      const startedPct = Math.round((enrollmentFunnel.started / enrollmentFunnel.enrolled) * 100);
      if (startedPct < 50) {
        out.push({ tone: 'info', icon: <Activity size={15} />, text: `${startedPct}% of enrolled learners have started their courses.` });
      }
    }
    if (assessmentScores && assessmentScores.total > 0 && assessmentScores.passRate < 50) {
      out.push({ tone: 'warning', icon: <AlertTriangle size={15} />, text: `Assessment pass rate is ${assessmentScores.passRate}% — review difficulty or content.` });
    }
    return out.slice(0, 4);
  }, [kpiSummary, enrollmentFunnel, assessmentScores]);

  // --- Leaderboard pagination ----------------------------------------------
  const filteredLeaderboard = useMemo(() => {
    const rows = leaderboardData || [];
    const q = lbSearch.trim().toLowerCase();
    return q ? rows.filter((e) => e.name?.toLowerCase().includes(q)) : rows;
  }, [leaderboardData, lbSearch]);

  const lbTotalPages = Math.max(1, Math.ceil(filteredLeaderboard.length / LEADERBOARD_PAGE_SIZE));
  const lbCurrentPage = Math.min(lbPage, lbTotalPages);
  const lbSlice = filteredLeaderboard.slice(
    (lbCurrentPage - 1) * LEADERBOARD_PAGE_SIZE,
    lbCurrentPage * LEADERBOARD_PAGE_SIZE,
  );
  useEffect(() => { setLbPage(1); }, [lbSearch]);

  const handleGenerateReport = (reportName: string) => {
    setSelectedReport(reportName);
    setShowModal(true);
  };

  const handleFormatToggle = (format: string) => {
    setSelectedFormats((prev) => prev.includes(format) ? prev.filter((f) => f !== format) : [...prev, format]);
  };
  const handleDepartmentToggle = (dept: string) => {
    setSelectedDepartments((prev) => prev.includes(dept) ? prev.filter((d) => d !== dept) : [...prev, dept]);
  };
  const handleSectionToggle = (section: string) => {
    setSelectedSections((prev) => prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]);
  };

  const validateDates = (): boolean => {
    if ((fromDate && !toDate) || (!fromDate && toDate)) {
      toast.error('Please select both From and To dates');
      return false;
    }
    if (fromDate && toDate && fromDate > toDate) {
      toast.error('From Date must be on or before To Date');
      return false;
    }
    return true;
  };

  const handleGenerateNow = async () => {
    const reportType = REPORT_TYPES[selectedReport];
    if (!reportType) { toast.error('Unknown report type'); return; }
    if (selectedFormats.length === 0) { toast.error('Select at least one output format'); return; }
    if (!validateDates()) return;

    if (schedulingEnabled) {
      const recipients = scheduleRecipients.split(',').map((s) => s.trim()).filter(Boolean);
      if (recipients.length === 0) { toast.error('Add at least one recipient email to schedule this report'); return; }
      try {
        await createSchedule({
          scheduleName: selectedReport,
          frequency: scheduleFrequency,
          timeOfDay: scheduleTime,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          recipients,
          reportFormat: (selectedFormats[0] || 'PDF').toLowerCase(),
        });
        toast.success('Report scheduled successfully');
        setShowModal(false);
      } catch (err: any) {
        toast.error(err?.message || 'Failed to schedule report');
      }
      return;
    }

    const dateRange = fromDate && toDate ? { from: fromDate, to: toDate } : undefined;
    const department = selectedDepartments.length ? selectedDepartments[0] : undefined;
    let allOk = true;
    for (const fmt of selectedFormats) {
      const res = await generateAndDownloadReport({
        reportType, format: fmt.toLowerCase() as GenerateReportParams['format'], dateRange, department,
      });
      if (!res.success) { allOk = false; toast.error(res.error || `Failed to generate ${fmt} report`); }
    }
    if (allOk) { toast.success('Report generated successfully'); setShowModal(false); }
  };

  const handleExportAll = async () => {
    const types = Object.values(REPORT_TYPES);
    toast.info('Generating all reports…');
    let anyFail = false;
    for (const reportType of types) {
      const res = await generateAndDownloadReport({ reportType, format: 'pdf' });
      if (!res.success) anyFail = true;
    }
    if (anyFail) toast.error('Some reports failed to export');
    else toast.success('All reports exported');
  };

  const handleRefresh = () => {
    fetchLeaderboardData();
    fetchDepartmentPerformance();
    fetchSkillsAssessment();
    fetchCompletionTrends();
    fetchCertificationDistribution();
    fetchExtendedAnalytics();
    toast.info('Refreshing analytics…');
  };

  const nf = (n: number | undefined) => (n ?? 0).toLocaleString();

  return (
    <div className="s-admin-reports-container">
      <div className="s-admin-reports-header">
        <div className="s-admin-reports-header-top">
          <div className="s-admin-reports-title-section">
            <h1>Reports & Analytics</h1>
            <p>Comprehensive insights into learning performance and progress</p>
          </div>
          <div className="s-admin-reports-actions">
            <button className="s-admin-reports-btn ghost" onClick={handleRefresh} disabled={analyticsLoading}>
              <RefreshCw size={16} className={analyticsLoading ? 's-admin-spin' : ''} />
              Refresh
            </button>
            <button className="s-admin-reports-btn primary" onClick={handleExportAll} disabled={generating}>
              <Download size={18} />
              {generating ? 'Exporting…' : 'Export All'}
            </button>
          </div>
        </div>
      </div>

      {/* ===== KPI BAND ===== */}
      <div className="s-admin-kpi-band">
        <StatCard
          label="Total Learners" tone="blue" icon={<Users size={20} />}
          value={analyticsLoading && !kpiSummary ? '—' : nf(kpiSummary?.totalLearners)}
          delta={kpiSummary ? pctChange(kpiSummary.newLearnersThisMonth, kpiSummary.newLearnersLastMonth) : null}
          sub={kpiSummary ? `+${nf(kpiSummary.newLearnersThisMonth)} this month` : undefined}
        />
        <StatCard
          label="Active This Week" tone="green" icon={<Activity size={20} />}
          value={analyticsLoading && !kpiSummary ? '—' : nf(kpiSummary?.activeThisWeek)}
          delta={kpiSummary ? pctChange(kpiSummary.activeThisWeek, kpiSummary.activePrevWeek) : null}
          sub={kpiSummary && kpiSummary.totalLearners ? `${Math.round((kpiSummary.activeThisWeek / kpiSummary.totalLearners) * 100)}% of learners` : undefined}
        />
        <StatCard
          label="Completion Rate" tone="purple" icon={<Award size={20} />}
          value={analyticsLoading && !kpiSummary ? '—' : `${kpiSummary?.completionRate ?? 0}%`}
          sub={kpiSummary ? `${nf(kpiSummary.completedEnrollments)} of ${nf(kpiSummary.totalEnrollments)} enrolments` : undefined}
        />
        <StatCard
          label="Avg Time / Learner" tone="teal" icon={<Clock size={20} />}
          value={analyticsLoading && !kpiSummary ? '—' : `${kpiSummary?.avgTimePerLearnerMinutes ?? 0}m`}
          sub="across all courses"
        />
        <StatCard
          label="Certificates Issued" tone="orange" icon={<GraduationCap size={20} />}
          value={analyticsLoading && !kpiSummary ? '—' : nf(kpiSummary?.certificatesIssued)}
          sub={kpiSummary && kpiSummary.certsExpiringSoon > 0 ? `${kpiSummary.certsExpiringSoon} expiring ≤30d` : 'all valid'}
        />
        <StatCard
          label="Avg Assessment Score" tone="red" icon={<Zap size={20} />}
          value={analyticsLoading && !kpiSummary ? '—' : `${kpiSummary?.avgAssessmentScore ?? 0}%`}
          sub={kpiSummary ? `${nf(kpiSummary.assessmentResponses)} responses` : undefined}
        />
      </div>

      {/* ===== INSIGHTS ===== */}
      <InsightStrip insights={insights} />

      {/* ===== REPORT GENERATOR CARDS ===== */}
      <div className="s-admin-reports-cards">
        {REPORT_CARDS.map((card) => (
          <div className="s-admin-report-card" key={card.name}>
            <div className="s-admin-report-card-header">
              <div className={`s-admin-report-icon s-admin-${card.tone}`}>{card.icon}</div>
              <div className="s-admin-report-info">
                <h3>{card.name}</h3>
                <p>{card.description}</p>
              </div>
            </div>
            <div className="s-admin-report-formats">
              <span className="s-admin-format-badge s-admin-pdf">📄 PDF</span>
              <span className="s-admin-format-badge s-admin-excel">📊 EXCEL</span>
              <span className="s-admin-format-badge s-admin-csv">🧾 CSV</span>
            </div>
            <button className="s-admin-generate-btn" onClick={() => handleGenerateReport(card.name)}>
              <Download size={18} />
              Generate Report
            </button>
          </div>
        ))}
      </div>

      {/* ===== CHART GRID ===== */}
      <div className="s-admin-charts-grid">
        <ChartCard title="Department Performance" subtitle="Engagement & coverage by department" png
          loading={!departmentPerformance} empty={!!departmentPerformance && departmentPerformance.length === 0}
          csv={{ filename: 'department-performance', rows: departmentPerformance || [] }}>
          <DepartmentBar data={departmentPerformance || []} />
        </ChartCard>

        <ChartCard title="Skills Assessment" subtitle="Average completion across skill areas" png
          loading={!skillsAssessment} empty={!!skillsAssessment && skillsAssessment.length === 0}
          csv={{ filename: 'skills-assessment', rows: skillsAssessment || [] }}>
          <SkillsRadar data={skillsAssessment || []} />
        </ChartCard>
      </div>

      <div className="s-admin-charts-grid">
        <ChartCard title="Completion Trends" subtitle="Monthly completion vs enrolment" png
          loading={!completionTrends} empty={!!completionTrends && completionTrends.length === 0}
          csv={{ filename: 'completion-trends', rows: completionTrends || [] }}>
          <CompletionArea data={completionTrends || []} />
        </ChartCard>

        <ChartCard title="Certification Distribution" subtitle="Types of certificates earned" png
          loading={!certificationDistribution} empty={!!certificationDistribution && certificationDistribution.length === 0}
          csv={{ filename: 'certification-distribution', rows: certificationDistribution || [] }}>
          <CertPie data={certificationDistribution || []} />
        </ChartCard>
      </div>

      <div className="s-admin-charts-grid">
        <ChartCard title="Enrolment Funnel" subtitle="Enrolled → started → completed → certified" png
          loading={analyticsLoading && !enrollmentFunnel} empty={!!enrollmentFunnel && enrollmentFunnel.enrolled === 0}>
          {enrollmentFunnel && <FunnelViz data={enrollmentFunnel} />}
        </ChartCard>

        <ChartCard title="Time Spent Distribution" subtitle={`Learners by total minutes${timeDistribution ? ` · avg ${timeDistribution.avgMinutes}m` : ''}`} png
          loading={analyticsLoading && !timeDistribution} empty={!!timeDistribution && timeDistribution.buckets.every((b) => b.learners === 0)}
          csv={timeDistribution ? { filename: 'time-distribution', rows: timeDistribution.buckets } : undefined}>
          {timeDistribution && <TimeHistogram data={timeDistribution} />}
        </ChartCard>
      </div>

      <div className="s-admin-charts-grid">
        <ChartCard title="Assessment Scores" subtitle={assessmentScores ? `Avg ${assessmentScores.avgScore}% · ${assessmentScores.passRate}% pass rate` : 'Score distribution'} png
          loading={analyticsLoading && !assessmentScores} empty={!!assessmentScores && assessmentScores.total === 0}
          csv={assessmentScores ? { filename: 'assessment-scores', rows: assessmentScores.buckets } : undefined}>
          {assessmentScores && <AssessmentDist data={assessmentScores} />}
        </ChartCard>

        <ChartCard title="Learner Levels" subtitle="Gamification level distribution" png
          loading={analyticsLoading && !levelDistribution} empty={!!levelDistribution && levelDistribution.levels.length === 0}
          csv={levelDistribution ? { filename: 'level-distribution', rows: levelDistribution.levels } : undefined}>
          {levelDistribution && <LevelDonut data={levelDistribution} />}
        </ChartCard>
      </div>

      {/* Activity heatmap — full width */}
      <div className="s-admin-charts-grid single">
        <ChartCard title="Login Activity Heatmap" subtitle="Logins by day & time of day (last 90 days)"
          loading={analyticsLoading && !activityHeatmap} empty={!!activityHeatmap && activityHeatmap.length === 0}
          csv={activityHeatmap ? { filename: 'activity-heatmap', rows: activityHeatmap } : undefined}>
          <ActivityHeatmap data={activityHeatmap || []} />
        </ChartCard>
      </div>

      <div className="s-admin-charts-grid">
        <ChartCard title="Cohort Retention" subtitle="Signup cohorts: enrolled / completed / active"
          loading={analyticsLoading && !cohortRetention} empty={!!cohortRetention && cohortRetention.length === 0}
          csv={cohortRetention ? { filename: 'cohort-retention', rows: cohortRetention } : undefined}>
          <CohortHeatmap data={cohortRetention || []} />
        </ChartCard>

        <ChartCard title="Certification Expiry" subtitle="Validity & upcoming renewals"
          loading={analyticsLoading && !certificationExpiry}>
          {certificationExpiry && <CertExpiryPanel data={certificationExpiry} />}
        </ChartCard>
      </div>

      <div className="s-admin-charts-grid">
        <ChartCard title="Team Performance" subtitle="Completion & engagement by team"
          loading={analyticsLoading && !teamPerformance} empty={!!teamPerformance && teamPerformance.length === 0}
          csv={teamPerformance ? { filename: 'team-performance', rows: teamPerformance } : undefined}>
          <TeamTable data={teamPerformance || []} />
        </ChartCard>

        <ChartCard title="Top Streaks" subtitle="Most consistent learners"
          loading={analyticsLoading && !levelDistribution}
          empty={!!levelDistribution && levelDistribution.topStreaks.length === 0}>
          {levelDistribution && <StreakList data={levelDistribution} />}
        </ChartCard>
      </div>

      {/* ===== LEADERBOARD ===== */}
      <div className="s-admin-leaderboard-card">
        <div className="s-admin-leaderboard-header">
          <div className="s-admin-leaderboard-icon"><Award size={28} /></div>
          <div className="s-admin-leaderboard-title">
            <h3>Leaderboard</h3>
            <p>Top performing learners</p>
          </div>
          <div className="s-admin-leaderboard-search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search learner…"
              value={lbSearch}
              onChange={(e) => setLbSearch(e.target.value)}
            />
          </div>
        </div>

        <table className="s-admin-leaderboard-table">
          <thead>
            <tr>
              <th>Rank</th><th>Employee Name</th><th>Level</th><th>Points</th><th>Courses</th><th>Certificates</th>
            </tr>
          </thead>
          <tbody>
            {lbSlice.length === 0 ? (
              <tr><td colSpan={6} className="s-admin-lb-empty">No learners found</td></tr>
            ) : lbSlice.map((emp) => (
              <tr key={emp.rank}>
                <td>
                  <div className="s-admin-rank-cell">
                    {emp.rank === 1 && '🏆'}
                    {emp.rank === 2 && '🥈'}
                    {emp.rank === 3 && '🥉'}
                    {emp.rank > 3 && emp.rank}
                    {emp.trend === 'up' && <span className="s-admin-trend-icon up">▲</span>}
                    {emp.trend === 'down' && <span className="s-admin-trend-icon down">▼</span>}
                    {emp.trend === 'same' && <span className="s-admin-trend-icon same">−</span>}
                  </div>
                </td>
                <td>
                  <div className="s-admin-employee-cell">
                    <div className="s-admin-employee-avatar">{emp.initials}</div>
                    <span>{emp.name}</span>
                  </div>
                </td>
                <td>
                  <div className="s-admin-level-cell">
                    <span className="s-admin-level-badge" style={{ background: emp.levelColor }}>{emp.level}</span>
                    <div className="s-admin-level-progress">
                      <div className="s-admin-progress-bar">
                        <div className="s-admin-progress-fill" style={{ width: `${emp.progress}%`, background: emp.levelColor }} />
                      </div>
                      <div className="s-admin-progress-text">{emp.progressText}</div>
                    </div>
                  </div>
                </td>
                <td><strong>{emp.points}</strong></td>
                <td>{emp.coursesCompleted}</td>
                <td>{emp.certificates}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="s-admin-pagination">
          <div className="s-admin-pagination-info">
            <span>
              {filteredLeaderboard.length === 0 ? '0' :
                `${(lbCurrentPage - 1) * LEADERBOARD_PAGE_SIZE + 1}–${Math.min(lbCurrentPage * LEADERBOARD_PAGE_SIZE, filteredLeaderboard.length)}`}
              {' '}of {filteredLeaderboard.length}
            </span>
          </div>
          <div className="s-admin-pagination-buttons">
            <button className="s-admin-page-btn" disabled={lbCurrentPage <= 1} onClick={() => setLbPage(lbCurrentPage - 1)}>‹</button>
            {Array.from({ length: lbTotalPages }, (_, i) => i + 1)
              .filter((p) => Math.abs(p - lbCurrentPage) <= 2 || p === 1 || p === lbTotalPages)
              .map((p, idx, arr) => (
                <React.Fragment key={p}>
                  {idx > 0 && p - arr[idx - 1] > 1 && <span className="s-admin-page-ellipsis">…</span>}
                  <button className={`s-admin-page-btn ${p === lbCurrentPage ? 'active' : ''}`} onClick={() => setLbPage(p)}>{p}</button>
                </React.Fragment>
              ))}
            <button className="s-admin-page-btn" disabled={lbCurrentPage >= lbTotalPages} onClick={() => setLbPage(lbCurrentPage + 1)}>›</button>
          </div>
        </div>
      </div>

      {/* ===== GENERATE MODAL ===== */}
      {showModal && (
        <div className="s-admin-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="s-admin-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="s-admin-modal-left">
              <div className="s-admin-modal-header">
                <div className="s-admin-modal-icon"><Users size={24} /></div>
                <div className="s-admin-modal-title">
                  <h2>Generate Report</h2>
                  <p>{selectedReport}</p>
                </div>
                <button className="s-admin-modal-close" onClick={() => setShowModal(false)}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <div className="s-admin-modal-section">
                <div className="s-admin-section-header" onClick={() => setShowBasicConfig(!showBasicConfig)}>
                  <div className="s-admin-section-header-left">
                    <div className="s-admin-section-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                      </svg>
                    </div>
                    <span className="s-admin-section-title">Basic Configuration</span>
                  </div>
                  <svg className={`s-admin-section-chevron ${showBasicConfig ? 's-admin-open' : ''}`} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                {showBasicConfig && (
                  <div className="s-admin-section-content">
                    <div className="s-admin-form-group">
                      <label className="s-admin-form-label">From Date</label>
                      <input type="date" className="s-admin-date-input" value={fromDate} max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)} />
                    </div>
                    <div className="s-admin-form-group">
                      <label className="s-admin-form-label">To Date</label>
                      <input type="date" className="s-admin-date-input" value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} />
                    </div>
                    <div className="s-admin-form-group">
                      <label className="s-admin-form-label">Output Format(s) <span>*</span></label>
                      <div className="s-admin-format-grid">
                        {['PDF', 'EXCEL'].map((fmt) => (
                          <div key={fmt} className={`s-admin-format-card ${selectedFormats.includes(fmt) ? 's-admin-selected' : ''}`} onClick={() => handleFormatToggle(fmt)}>
                            <div className="s-admin-format-card-icon">{fmt === 'PDF' ? '📄' : '📊'}</div>
                            <div className="s-admin-format-card-name">{fmt}</div>
                            <div className="s-admin-format-card-check">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="s-admin-modal-footer">
                <div className="s-admin-footer-info">
                  {selectedSections.length} sections • {selectedFormats.length} format(s)
                </div>
                <div className="s-admin-modal-actions">
                  <button className="s-admin-modal-btn cancel" onClick={() => setShowModal(false)}>Cancel</button>
                  <button className="s-admin-modal-btn generate" onClick={handleGenerateNow} disabled={generating}>
                    <Download size={18} />
                    {generating ? 'Generating…' : schedulingEnabled ? 'Schedule Report' : 'Generate Now'}
                  </button>
                </div>
              </div>
            </div>

            <div className="s-admin-modal-right">
              <div className="s-admin-filter-section-header" onClick={() => setShowFiltersSegments(!showFiltersSegments)}>
                <Filter size={18} color="#f97316" />
                <span style={{ fontWeight: 600, fontSize: '14px' }}>Filters & Segments</span>
                <svg style={{ marginLeft: 'auto' }} className={`s-admin-section-chevron ${showFiltersSegments ? 's-admin-open' : ''}`} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>

              {showFiltersSegments && (
                <>
                  <label className="s-admin-form-label">Departments (Optional)</label>
                  <div className="s-admin-departments-grid">
                    {deptOptions.map((dept) => (
                      <div key={dept} className="s-admin-checkbox-item" onClick={() => handleDepartmentToggle(dept)}>
                        <div className={`s-admin-checkbox ${selectedDepartments.includes(dept) ? 's-admin-checked' : ''}`}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                        </div>
                        <span className="s-admin-checkbox-label">{dept}</span>
                      </div>
                    ))}
                  </div>
                  <div className="s-admin-helper-text">
                    {selectedDepartments.length ? `${selectedDepartments.length} selected` : 'All departments will be included'}
                  </div>
                </>
              )}

              <div className="s-admin-container-section" style={{ marginTop: '24px' }}>
                <div className="s-admin-container-header" onClick={() => setShowReportSectionsPanel(!showReportSectionsPanel)} style={{ cursor: 'pointer' }}>
                  <div className="s-admin-container-title">
                    <FileText size={18} color="#f97316" />
                    <span>Report Sections</span>
                  </div>
                  <svg className={`s-admin-collapse-icon s-admin-section-chevron ${showReportSectionsPanel ? 's-admin-open' : ''}`} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                {showReportSectionsPanel && (
                  <div className="s-admin-section-items">
                    {ALL_SECTIONS.map((section) => {
                      const checked = selectedSections.includes(section);
                      return (
                        <div key={section} className="s-admin-section-item" onClick={() => handleSectionToggle(section)}>
                          <div className="s-admin-section-checkbox" style={checked ? undefined : { background: 'transparent', borderColor: 'var(--color-border)' }}>
                            {checked && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
                          </div>
                          <span className="s-admin-section-item-label">{section}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="s-admin-container-section">
                <div className="s-admin-container-header" onClick={() => setShowSchedulePanel(!showSchedulePanel)} style={{ cursor: 'pointer' }}>
                  <div className="s-admin-container-title">
                    <Calendar size={18} color="#f97316" />
                    <span>Schedule & Distribution</span>
                  </div>
                  <svg className={`s-admin-collapse-icon s-admin-section-chevron ${showSchedulePanel ? 's-admin-open' : ''}`} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                {showSchedulePanel && (
                  <>
                    <div className="s-admin-schedule-item" onClick={() => setSchedulingEnabled(!schedulingEnabled)} style={{ cursor: 'pointer' }}>
                      <div className="s-admin-schedule-checkbox" style={schedulingEnabled ? { background: 'var(--color-sunflare)', borderColor: 'var(--color-sunflare)' } : undefined}>
                        {schedulingEnabled && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
                      </div>
                      <div className="s-admin-schedule-info">
                        <h4>Enable Automatic Scheduling</h4>
                        <p>Automatically generate and send this report</p>
                      </div>
                    </div>

                    {schedulingEnabled && (
                      <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div className="s-admin-form-group">
                          <label className="s-admin-form-label">Frequency</label>
                          <select className="s-admin-date-input" value={scheduleFrequency} onChange={(e) => setScheduleFrequency(e.target.value as ScheduleFrequency)}>
                            <option value="daily">Daily</option>
                            <option value="weekdays">Weekdays</option>
                            <option value="weekly">Weekly</option>
                            <option value="biweekly">Bi-weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="once">Once</option>
                          </select>
                        </div>
                        <div className="s-admin-form-group">
                          <label className="s-admin-form-label">Time</label>
                          <input type="time" className="s-admin-date-input" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} />
                        </div>
                        <div className="s-admin-form-group">
                          <label className="s-admin-form-label">Recipient emails <span>*</span></label>
                          <input type="text" className="s-admin-date-input" placeholder="alice@company.com, bob@company.com" value={scheduleRecipients} onChange={(e) => setScheduleRecipients(e.target.value)} />
                          <div className="s-admin-helper-text">Comma-separated email addresses</div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsAnalytics;
